import { computed } from '@preact/signals';
import {
  canFinance,
  financeDurationOptions,
  minDownPayment,
  roundCents,
  type RatedProductWire,
} from 'shared';
import { financeLimits } from '@/state/config.js';
import { cart, findRatedTier } from '@/state/session.js';
import { selectedSurchargeSum } from '@/lib/surcharges.js';
import { monthlyEstimate, parseDownPayment, validateDownPayment } from '@/lib/finance.js';
import type { CartEntry, CartLine } from '@/lib/widget-types.js';

/** Rounded per line item, in the same order the server does: sale price first, then tax off
 *  the *rounded* sale price. `/api/rate` sends full-precision floats, but checkout persists
 *  `roundCents(salePrice)` and bills Budco `roundCents(salePrice * taxRate)` — matching that
 *  here is what keeps the displayed totals equal to the charge on a multi-item cart. */
function buildLine(entry: CartEntry, index: number, tier: RatedProductWire): CartLine {
  const isFinance = entry.payment.type === 'finance';
  const salePrice = roundCents(isFinance ? tier.sale_price_finance : tier.sale_price_pif);
  const surcharges = roundCents(selectedSurchargeSum(tier, entry.surcharges));
  const taxes = roundCents(salePrice * tier.tax_rate);
  const financeWholesale = roundCents(tier.sale_price_finance) + surcharges;
  const limits = financeLimits.value;

  const base = {
    index,
    entry,
    tier,
    salePrice,
    taxes,
    surcharges,
    total: salePrice + taxes + surcharges,
    pifWholesale: roundCents(tier.sale_price_pif) + surcharges,
    financeWholesale,
    durationOptions: financeDurationOptions(tier.term_months, limits),
    canFinance: canFinance(tier.term_months, limits),
    minDown: minDownPayment(financeWholesale, limits),
  };

  if (entry.payment.type !== 'finance') {
    return {
      ...base,
      downPayment: null,
      downPaymentError: null,
      monthly: null,
      dueToday: base.total,
    };
  }

  const downPayment = parseDownPayment(entry.payment.downPaymentInput);
  const downPaymentError = validateDownPayment(downPayment, financeWholesale, limits);
  return {
    ...base,
    downPayment,
    downPaymentError,
    monthly:
      downPaymentError === null && downPayment !== null
        ? monthlyEstimate(base.total, downPayment, entry.payment.durationMonths)
        : null,
    dueToday: downPayment ?? 0,
  };
}

/** Entries whose tier no longer resolves are dropped — a stale cart is caught at submit. */
export const cartLines = computed<CartLine[]>(() =>
  cart.value.flatMap((entry, index) => {
    const tier = findRatedTier(entry.quoteItemId);
    return tier === undefined ? [] : [buildLine(entry, index, tier)];
  }),
);

interface CartTotals {
  salePrice: number;
  taxes: number;
  surcharges: number;
  total: number;
}

export const cartGrandTotal = computed<CartTotals>(() =>
  cartLines.value.reduce(
    (sum, l) => ({
      salePrice: sum.salePrice + l.salePrice,
      taxes: sum.taxes + l.taxes,
      surcharges: sum.surcharges + l.surcharges,
      total: sum.total + l.total,
    }),
    { salePrice: 0, taxes: 0, surcharges: 0, total: 0 },
  ),
);

/** What Budco collects at checkout. With a mixed cart this is the number that matters. */
export const cartDueToday = computed<number>(() =>
  cartLines.value.reduce((sum, l) => sum + l.dueToday, 0),
);

/** Null when nothing is financed, or when a line is still being typed. */
export const cartMonthlyTotal = computed<number | null>(() => {
  const financed = cartLines.value.filter((l) => l.entry.payment.type === 'finance');
  if (financed.length === 0) return null;
  let sum = 0;
  for (const line of financed) {
    if (line.monthly === null) return null;
    sum += line.monthly;
  }
  return sum;
});

/** True when every financed line carries a down payment we'd actually submit. */
export const cartFinanceValid = computed<boolean>(() =>
  cartLines.value.every((l) => l.downPaymentError === null),
);

/** Searched, not indexed into: `cartLines` drops entries whose tier a re-rate retired. */
export function findCartLine(index: number): CartLine | undefined {
  return cartLines.value.find((l) => l.index === index);
}
